import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useNotification } from "../components/Notifications";
import { apiFetch } from "../api/client";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";

function Register() {
    const [form, setForm] = useState({
        email: "",
        businessName: "",
        password: "",
    });

    const navigate = useNavigate();
    const notify = useNotification();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [agree, setAgree] = useState(true);

    // Handling Input change for registration form.
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
        setErrors((prev) => ({ ...prev, [name]: null }));
    };
    // Register User
    const registerUser = async () => {
        // client-side validation
        const nextErrors = {};
        if (!form.businessName) nextErrors.businessName = "Business name is required.";
        if (!form.email) nextErrors.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = "Invalid email format.";
        if (!form.password) nextErrors.password = "Password is required.";
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            notify("error", { title: "Validation", text: "Please fix validation errors." });
            // focus first invalid field
            const first = Object.keys(nextErrors)[0];
            const el = document.getElementsByName(first)[0];
            if (el && typeof el.focus === 'function') el.focus();
            return;
        }

        if (loading) return;
        setLoading(true);

        try {
            const res = await apiFetch(`/api/account/register`, {
                // Ensure register is not treated as tenant-scoped when creating a new tenant
                skipTenant: true,
                method: "POST",
                body: JSON.stringify({
                    email: form.email,
                    businessName: form.businessName,
                    password: form.password,
                }),
            });

            if (!res.ok) {
                let parsed = null;
                try {
                    parsed = await res.json();
                } catch (err) {
                    notify("error", { title: "Registration failed", text: res.statusText });
                    return;
                }
                const note = mapApiErrorToNotification(parsed, res.status);
                notify("error", note);

                return;
            }

            const data = await res.json();
            if (data?.succeeded) {
                notify("success", { title: "Registration successful", text: data.message || "Check your email to verify account." });
                // If backend returned tenant slug, persist it so subsequent requests set X-Tenant header
                const tenantSlug = data?.data?.tenantSlug || data?.data?.TenantSlug || data?.tenantSlug || data?.TenantSlug || null;
                if (tenantSlug) {
                    try {
                        localStorage.setItem("tenant", tenantSlug);
                    } catch (e) {
                        // ignore storage errors
                    }
                }
                // navigate to verify page; prefer tenant-prefixed path when tenantSlug is available
                const tenantPrefix = (process.env.REACT_APP_TENANT_PREFIX || "t").trim();
                if (tenantSlug) {
                    navigate(`/${tenantPrefix}/${tenantSlug}/verify-email?email=${encodeURIComponent(form.email)}`);
                } else {
                    navigate(`/verify-email?email=${encodeURIComponent(form.email)}`);
                }
            } else {
                const note = mapApiErrorToNotification(data, res.status);
                notify("error", note);
            }
        } catch (err) {
            console.error(err);
            notify("error", { title: "Network error", text: "Could not reach server." });
        } finally {
            setLoading(false);
        }
    };
    // ------------------

    const handleSubmit = (e) => {
        e.preventDefault();
        registerUser();
    };

     return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 h-screen items-center place-items-center">
                <div className="w-full max-w-md space-y-8 p-6 sm:p-10 rounded-lg">
                    <div>
                        <img
                            className="mx-auto h-20 w-auto"
                            src={require("../assets/stokraze.png")}
                            alt="Stokraze"
                        />

                        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
                            Register your account
                        </h2>
                    </div>

                    <form
                        className="mt-8 space-y-6"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        <div className="flex flex-col gap-4 rounded-md shadow-sm">

                            {/* Business Name */}
                            <div>
                                <input
                                    name="businessName"
                                    type="text"
                                    required
                                    className="relative block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-purple-600"
                                    placeholder="Business Name"
                                    value={form.businessName}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />

                                {errors.businessName && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {errors.businessName}
                                    </p>
                                )}
                            </div>

                            {/* Email */}
                            <div>
                                <input
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="relative block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-purple-600"
                                    placeholder="Email Address"
                                    value={form.email}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />

                                {errors.email && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            {/* Password */}
                            <div>
                                <input
                                    name="password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    className="relative block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-purple-600"
                                    placeholder="Password"
                                    value={form.password}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />

                                {errors.password && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {errors.password}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-purple-600"
                                    checked={agree}
                                    onChange={(e) =>
                                        setAgree(e.target.checked)
                                    }
                                />

                                <label
                                    htmlFor="remember-me"
                                    className="ml-2 block text-sm text-gray-900"
                                >
                                    I Agree Terms & Conditions
                                </label>
                            </div>

                            <div className="text-sm">
                                <Link
                                    to="/forgot-password"
                                    className="font-medium text-purple-600 hover:text-purple-500"
                                >
                                    Forgot your password?
                                </Link>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="group relative flex items-center justify-center w-full rounded-md bg-purple-600 py-3 px-4 text-white hover:bg-purple-500"
                            >
                                {loading
                                    ? "Registering..."
                                    : "Sign up"}
                            </button>

                            <p className="mt-2 text-center text-sm text-gray-600">
                                Already Have an Account?
                                <Link
                                    to="/login"
                                    className="font-medium text-purple-600 ml-1"
                                >
                                    Sign In
                                </Link>
                            </p>
                        </div>
                    </form>
                </div>

                <div className="flex justify-center order-first sm:order-last p-4">
                    <img
                        className="block sm:hidden max-w-full h-auto w-11/12 max-w-sm object-contain"
                        src={require("../assets/SignUp2.png")}
                        alt=""
                    />

                    <img
                        className="hidden sm:block"
                        src={require("../assets/SignUp2.png")}
                        alt=""
                    />
                </div>
            </div>
        </>
    );
}

export default Register;
